import type { SupportChatContext } from '@elara/client-core/lib/support-chat';

import { buildSupportEmailUrl, buildSupportWhatsappUrl } from '@elara/client-core/lib/support-chat';

import { useSessionState, useTripsState, useUserProfileState } from '#states/hooks.js';

/**
 * The traveler's name and the trip they're currently looking at — the context every support handoff
 * carries, built in exactly one place so WhatsApp and email give the concierge the same thing.
 */
function useSupportChatContext(): () => SupportChatContext {
  const sessionState = useSessionState();
  const userProfileState = useUserProfileState();
  const tripsState = useTripsState();

  return () => ({
    name: sessionState.user?.displayName ?? userProfileState.profile?.displayName,
    tripName: tripsState.current?.title,
    tripId: tripsState.current?.id,
  });
}

/**
 * Hands the traveler off to Shannon on WhatsApp (ELARA-750), carrying their name and the
 * trip they're currently looking at so the concierge has context.
 *
 * Shared by every support entry point — the nav launcher ({@link SupportButton}) and the
 * advisor introduction on the trips page.
 */
export function useOpenSupportChat(): () => void {
  const supportContext = useSupportChatContext();

  return () => {
    window.open(buildSupportWhatsappUrl(supportContext()), '_blank', 'noopener,noreferrer');
  };
}

/**
 * Opens the traveler's mail client addressed to Elara support, with the same identity/trip context
 * as the WhatsApp handoff. The alternative for travelers who don't use WhatsApp.
 *
 * Targets `_self` rather than a new tab: a `mailto:` hands off to an external client, so a new tab
 * would be left behind empty.
 */
export function useOpenSupportEmail(): () => void {
  const supportContext = useSupportChatContext();

  return () => {
    window.open(buildSupportEmailUrl(supportContext()), '_self');
  };
}
